import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { WHATSAPP_URL } from "./content";
import { scrollToId } from "./Reveal";
import { HeroVisual } from "./HeroVisual";

const LINES = ["Entender o que", "você sente é o", "primeiro passo", "para transformar", "como você vive."];
const ease = [0.22, 1, 0.36, 1];

const MaskLine = ({ text, i }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className="block" initial={{ y: "105%" }} animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay: 0.35 + i * 0.12, ease }}>
      {i === 3 ? <>para <em className="text-clay">transformar</em></> : text}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 80, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 80, damping: 18 });
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="inicio" ref={ref} onMouseMove={onMove} data-testid="hero-section"
      className="relative min-h-screen pt-32 sm:pt-36 pb-16 overflow-hidden grain">
      <div className="absolute -top-32 -right-24 w-[520px] h-[520px] rounded-full bg-butter/70 blur-3xl" />
      <div className="absolute bottom-0 -left-32 w-[420px] h-[420px] rounded-full bg-blush/70 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <motion.div style={{ y: yText }} className="lg:col-span-7">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}
            className="inline-flex items-center gap-2 rounded-full border border-sage/20 bg-cream/70 px-4 py-2 text-xs uppercase tracking-[0.24em] text-sage font-semibold">
            <span className="w-2 h-2 rounded-full bg-[#4C8A5E] animate-pulse" /> Atendimento psicológico on-line
          </motion.p>
          <h1 data-testid="hero-title" className="mt-7 font-display text-[2.6rem] leading-[1.02] sm:text-6xl lg:text-[5.4rem] text-ink tracking-tight">
            {LINES.map((l, i) => <MaskLine key={l} text={l} i={i} />)}
          </h1>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9, ease }}>
            <p className="mt-8 max-w-xl text-base sm:text-lg text-ink/75 leading-relaxed">
              Psicoterapia baseada na TCC para <strong className="font-semibold text-ink">adolescentes e adultos</strong>, com um olhar especial para a comunicação entre pais e filhos.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <a data-testid="hero-whatsapp-btn" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer"
                className="group inline-flex items-center gap-3 rounded-full bg-ink text-cream pl-6 pr-2 py-2 hover:bg-sage transition-colors">
                Conversar no WhatsApp
                <span className="w-10 h-10 rounded-full bg-butter text-ink flex items-center justify-center transition-transform duration-500 group-hover:rotate-[-45deg]">
                  <ArrowDownRight size={18} />
                </span>
              </a>
              <button data-testid="hero-how-btn" onClick={() => scrollToId("como-funciona")}
                className="rounded-full border border-ink/20 px-6 py-3 text-ink hover:bg-mint transition-colors">
                Como funciona
              </button>
            </div>
          </motion.div>
        </motion.div>
        <HeroVisual rx={rx} ry={ry} progress={scrollYProgress} />
      </div>
    </section>
  );
};
